import React, { useEffect, useState } from "react";
import { PageHeader, Card, Tag } from "../components/UI";
import { QrCode, Smartphone, ArrowDown, Sparkles, RefreshCw, Trash2 } from "lucide-react";
import DataTable from "../components/DataTable";
import api from "../lib/api";

const STEP_LABEL = {
  awaiting_dpdp: "Awaiting DPDP consent",
  declined: "Declined consent",
  complete: "Consent granted",
  // Legacy steps (for any pre-migration rows)
  awaiting_name: "Awaiting name",
  awaiting_clinic: "Awaiting clinic",
  awaiting_city: "Awaiting city",
  awaiting_specialty: "Awaiting specialty",
};

export default function Onboarding() {
  const [qr, setQr] = useState(null);
  const [sessions, setSessions] = useState([]);

  async function fetchAll() {
    const [a, b] = await Promise.all([api.get("/onboarding/qr"), api.get("/onboarding/sessions")]);
    setQr(a.data); setSessions(b.data);
  }
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [a, b] = await Promise.all([api.get("/onboarding/qr"), api.get("/onboarding/sessions")]);
        if (alive) { setQr(a.data); setSessions(b.data); }
      } catch { /* swallow */ }
    };
    tick();
    const t = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  async function reset(mobile) {
    if (!window.confirm("Reset onboarding session?")) return;
    await api.post(`/onboarding/reset/${mobile}`);
    await fetchAll();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Acquisition"
        title="WhatsApp Onboarding Studio"
        subtitle="A 60-second WhatsApp ceremony — physios scan a QR (or tap a DSE-shared link) and our bot validates them against the Outlet ERP master, captures DPDP consent, then unlocks their Member ID (= ERP ID) and 500 welcome points."
        testId="onboarding-page"
        actions={<button onClick={fetchAll} className="btn-secondary flex items-center gap-2"><RefreshCw size={14} /> Refresh</button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* QR + share */}
        <Card title="Scan to onboard" subtitle="Print this QR for clinic counters, DSE rounds, expos and conferences." className="lg:col-span-1">
          {qr ? (
            <div className="text-center">
              <div className="inline-block p-4 surface bg-white">
                <img src={qr.qr_url} alt="WhatsApp Onboarding QR" className="w-56 h-56 mx-auto" data-testid="onboarding-qr-img" />
              </div>
              <div className="mt-4">
                <div className="small-caps text-slate-400">Or tap the link</div>
                <a href={qr.wa_link} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline font-mono text-[12px] break-all" data-testid="onboarding-wa-link">
                  {qr.wa_link}
                </a>
              </div>
              <div className="ai-surface mt-4 p-3 text-[12px] text-left flex items-start gap-2">
                <Sparkles size={14} className="mt-0.5 shrink-0" />
                <p>{qr.instruction}</p>
              </div>
            </div>
          ) : <div className="text-slate-400 text-sm">Loading QR…</div>}
        </Card>

        {/* The 3-step flow visual */}
        <Card title="The conversation" subtitle="What the physio sees in WhatsApp" className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { n: 1, t: "Mobile validation", b: "👋 We check the mobile against the Outlet ERP master. If unmapped → reply: 'Please contact your DSE to enroll your clinic.' (no record created)" },
              { n: 2, t: "Welcome + clinic recap", b: "✅ Found your clinic. We replay the outlet name + city so the physio confirms identity." },
              { n: 3, t: "DPDP consent (India 2023)", b: "Reply YES to accept WhatsApp updates · milestone alerts · DSE contact. Reply NO to decline (no further messages)." },
              { n: 4, t: "Member ID = ERP ID issued", b: "🎉 'Member ID: <ERP_OUTLET_ID>' (no new CBC-XXXXX is generated) · 500 welcome bonus credited.", highlight: true },
            ].map((s) => (
              <div key={s.n} className={`relative pl-5 ${s.highlight ? "ai-surface p-3" : "surface p-3"}`}>
                <div className={`absolute left-0 top-3 h-4 w-4 rounded-full text-[10px] flex items-center justify-center ${s.highlight ? "bg-teal-700 text-white" : "bg-slate-200 text-slate-700"}`}>{s.n}</div>
                <div className="font-medium text-[13px] ml-2">{s.t}</div>
                <p className="text-[12px] text-slate-600 ml-2 mt-1 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[12px] text-slate-500 flex items-center gap-2"><ArrowDown size={12} /> Total time: under 60 seconds. Zero forms. Zero app installs. ERP-validated only.</div>
        </Card>
      </div>

      {/* Live sessions table */}
      <Card title="Live onboarding sessions" subtitle="Real-time funnel — DSEs can see where each physio is in the flow.">
        <DataTable
          columns={[
            { key: "mobile", label: "Mobile", filter: "text", render: (r) => <span className="font-mono text-[12px]">{r.mobile}</span> },
            { key: "step", label: "Step", filter: "select",
              options: ["awaiting_dpdp", "complete", "declined", "awaiting_name", "awaiting_clinic", "awaiting_city", "awaiting_specialty"],
              render: (r) => <Tag tone={r.step === "complete" ? "emerald" : r.step === "declined" ? "rose" : "amber"}>{STEP_LABEL[r.step] || r.step}</Tag> },
            { key: "name", label: "Captured name", accessor: (r) => r.data?.name, filter: "text" },
            { key: "clinic_name", label: "Clinic", accessor: (r) => r.data?.clinic_name, filter: "text" },
            { key: "city", label: "City", accessor: (r) => r.data?.city, filter: "text" },
            { key: "specialty", label: "Specialty", accessor: (r) => r.data?.specialty, filter: "text" },
            { key: "started_at", label: "Started", render: (r) => new Date(r.started_at).toLocaleString() },
            { key: "actions", label: "", sortable: false, align: "right",
              render: (r) => r.step !== "complete" ? <button onClick={(e) => { e.stopPropagation(); reset(r.mobile); }} className="text-slate-400 hover:text-rose-600" title="Reset session"><Trash2 size={14} /></button> : null },
          ]}
          data={sessions} rowKey="id"
          searchableKeys={["mobile"]}
          exportFilename="cbc-onboarding"
          testIdPrefix="onboarding"
          initialSort={{ key: "started_at", dir: "desc" }}
        />
      </Card>
    </div>
  );
}
